import '../components/challenge-leaderboard';

import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators';

import { GetActivatedChallengeResponse } from '../types';
import { StateController } from '../helpers/state/state-management/state-controller';
import challengeData from '../helpers/state/challenge-data';
import { getActivatedChallengesForChallenge } from '../helpers/api/challenge-api';
import { getQueryStringParameters } from '../helpers/urls';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';

/**
 * Challenge Leaderboard View
 * 
 * Uses challenge-leaderboard component to display leaderboard
 * Accessed via view=challenge-leaderboard parameter
 */
@customElement('challenge-leaderboard-view-page')
export class ChallengeLeaderboardViewPage extends LitElement {
    amplitude_callsite: string = 'Challenge Leaderboard View Page';

    @state()
    private challengeId?: string;

    @state()
    private participants: GetActivatedChallengeResponse[] = [];

    @state()
    private isReady: boolean = false;

    @state()
    private errorMessage?: string;

    private readonly bindChallengeState = new StateController(this, challengeData);

    async firstUpdated() {
        await this.startup();
    }

    private topicKey?: string;

    private async startup() {
        const queryString = getQueryStringParameters();
        this.challengeId = queryString.get('challenge_id') ?? undefined;
        const topic_key = queryString.get('topic_key') ?? undefined;
        this.topicKey = topic_key;
        const topic_id = queryString.get('topic_id') ?? undefined;

        if (!this.challengeId) {
            this.errorMessage = 'No challenge_id provided in URL parameters.';
            this.isReady = true;
            return;
        }
        // if it is not intantiated, we need to instantiate it to get the topic name for the page title
        if (!topicData.isReady) {
            await topicData.instantiateTopicData('leaderboard-view', false, false, true, topic_id, topic_key);
            if (!topicData.topic) {
                this.errorMessage = 'Failed to load topic data. Please check your topic_id and topic_key.';
                this.isReady = true;
                return;
            }
            if (!pointCache.isReady) {
                await pointCache.instantiatePointCache(topicData.topic, topic_key ?? '');
            }
            if (!challengeData.isReady) {
                await challengeData.instantiateChallengeData(this.challengeId, topic_key);
            }
        }
        await this.loadLeaderboard();
        window.dispatchEvent(new CustomEvent('page-content-ready'));
    }

    private async loadLeaderboard() {
        if (!this.challengeId) {
            return;
        }

        try {
            // Get activated challenges (participants)
            this.participants = await getActivatedChallengesForChallenge(
                this.amplitude_callsite,
                this.challengeId,
                this.topicKey
            );

            // Sort by points descending
            this.participants.sort((a, b) => {
                const aPoints = a.point_count ?? 0;
                const bPoints = b.point_count ?? 0;
                return bPoints - aPoints;
            });

            this.isReady = true;
        } catch (error) {
            console.error('Error loading leaderboard:', error);
            this.errorMessage = 'Failed to load leaderboard. Please check your challenge_id.';
            this.isReady = true;
        }
    }

    render() {
        if (!challengeData.isReady) {
                return html`
                <div class="loading">
                    <loading-element text="Loading data..."></loading-element>
                </div>
            `;
        }
        return html`
            <div class="page-container">
                <challenge-leaderboard
                    .participants=${this.participants}
                    .challengeName=${challengeData.challenge?.name ?? 'Challenge'}
                    .loading=${!this.isReady}
                    .errorMessage=${this.errorMessage}
                ></challenge-leaderboard>
            </div>
        `;
    }

    static styles = css`
        :host {
            display: block;
            background: #ffffff;
            min-height: 100vh;
        }

        .page-container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 2rem;
        }

        @media (max-width: 768px) {
            .page-container {
                padding: 1rem;
            }
        }
    `;
}
